import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import { renameDoc } from "../../lib/storage";
import { decryptJSON } from "../../lib/crypto";
import { zipDocs, download, safeName } from "../../lib/exporters";

export const RenameDialog = ({ doc, onClose, onDone }) => {
  const [title, setTitle] = useState("");
  useEffect(() => { if (doc) setTitle(doc.title); }, [doc]);
  const save = async (e) => {
    e.preventDefault();
    await renameDoc(doc, title.trim() || "Untitled document");
    onClose();
    onDone();
  };
  return (
    <Dialog open={!!doc} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-sm" data-testid="rename-dialog">
        <DialogHeader><DialogTitle>Rename document</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <Input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} data-testid="rename-input" />
          <DialogFooter><Button type="submit" data-testid="rename-save-btn">Save</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const FORMATS = [["docx", "Word (.docx)"], ["pdf", "PDF (.pdf)"], ["txt", "Plain text (.txt)"]];

export const ZipDialog = ({ docs, onClose }) => {
  const [fmts, setFmts] = useState({ docx: true, pdf: false, txt: false });
  const [pws, setPws] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (docs) setPws({}); }, [docs]);
  const locked = (docs || []).filter((d) => d.encrypted);
  const chosen = Object.keys(fmts).filter((k) => fmts[k]);

  const build = async () => {
    setBusy(true);
    try {
      const items = [];
      for (const d of docs) {
        let body = d.body;
        if (d.encrypted) {
          try { body = await decryptJSON(d.cipher, pws[d.id] || ""); }
          catch { throw new Error(`Wrong password for “${d.title}”`); }
        }
        items.push({ title: d.title, ...body });
      }
      const blob = await zipDocs(items, chosen);
      download(blob, docs.length === 1 ? `${safeName(docs[0].title)}.zip` : `documents-${new Date().toISOString().slice(0, 10)}.zip`);
      toast.success(`ZIP ready · ${items.length} document${items.length > 1 ? "s" : ""}`);
      onClose();
    } catch (x) {
      toast.error(x.message || "ZIP export failed");
    }
    setBusy(false);
  };

  return (
    <Dialog open={!!docs} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="zip-dialog">
        <DialogHeader>
          <DialogTitle>Download as ZIP</DialogTitle>
          <DialogDescription>{docs?.length} document{docs?.length > 1 ? "s" : ""} · choose the formats to include.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2.5">
          {FORMATS.map(([k, label]) => (
            <label key={k} className="flex items-center gap-3 rounded-md border px-3 py-2.5 cursor-pointer hover:bg-accent/50">
              <Checkbox checked={fmts[k]} onCheckedChange={(v) => setFmts((f) => ({ ...f, [k]: !!v }))} data-testid={`zip-format-${k}`} />
              <span className="text-sm">{label}</span>
            </label>
          ))}
        </div>
        {locked.length > 0 && (
          <div className="space-y-2 pt-2">
            <p className="text-xs text-muted-foreground">Protected documents need their password. Files inside the ZIP are not encrypted.</p>
            {locked.map((d) => (
              <div key={d.id} className="space-y-1">
                <Label className="text-xs flex items-center gap-1.5"><Lock className="h-3 w-3 text-amber-600" />{d.title}</Label>
                <Input type="password" value={pws[d.id] || ""} onChange={(e) => setPws((p) => ({ ...p, [d.id]: e.target.value }))} data-testid={`zip-password-${d.id}`} />
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={build} disabled={busy || !chosen.length} data-testid="zip-download-btn">
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{busy ? "Building…" : "Download ZIP"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const ClearCacheDialog = ({ open, onClose }) => {
  const [wipe, setWipe] = useState(false);
  const [confirm, setConfirm] = useState("");
  useEffect(() => { if (open) { setWipe(false); setConfirm(""); } }, [open]);
  const run = async () => {
    sessionStorage.clear();
    if (window.caches) for (const k of await caches.keys()) await caches.delete(k);
    if (wipe) {
      for (const db of ["lightword-db", "lightword-signatures", "lightword-templates"]) indexedDB.deleteDatabase(db);
      const theme = localStorage.getItem("lw.theme");
      localStorage.clear();
      if (theme) localStorage.setItem("lw.theme", theme);
      toast.success("All local data deleted");
      setTimeout(() => window.location.reload(), 600);
    } else toast.success("Cache cleared — your documents are untouched");
    onClose();
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="clear-cache-dialog">
        <DialogHeader>
          <DialogTitle>Clear cache</DialogTitle>
          <DialogDescription>Removes temporary data (location & weather cache, offline app files). Documents, templates and saved signatures are kept.</DialogDescription>
        </DialogHeader>
        <label className="flex items-start gap-3 rounded-md border border-destructive/30 p-3 cursor-pointer">
          <Checkbox checked={wipe} onCheckedChange={(v) => setWipe(!!v)} data-testid="clear-cache-wipe-checkbox" className="mt-0.5" />
          <span className="text-sm"><b className="text-destructive">Also delete all documents, templates and signatures</b><span className="block text-xs text-muted-foreground">Cannot be undone. Download a library backup first.</span></span>
        </label>
        {wipe && <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder='Type "DELETE" to confirm' data-testid="clear-cache-confirm-input" />}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant={wipe ? "destructive" : "default"} disabled={wipe && confirm !== "DELETE"} onClick={run} data-testid="clear-cache-confirm-btn">{wipe ? "Delete everything" : "Clear cache"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
